import { createListenerMiddleware, TypedStartListening } from '@reduxjs/toolkit';
import { apiFetch } from '../../../lib/api';
import {
  ticketUpserted, commentsSnapshotReplaced, markLiveSubscribed, markLiveUnsubscribed
} from './ticketsSlice';
import type { RootState } from '../../index';
import type { Ticket, TicketComment } from './types';

export const ticketsListenerMiddleware = createListenerMiddleware();
type AppStartListening = TypedStartListening<RootState>;
const startListening = ticketsListenerMiddleware.startListening as AppStartListening;

const TICKET_POLL_MS = 7000;
const COMMENTS_POLL_MS = 5000;

// keep interval handles outside of Redux state
const ticketPollers = new Map<string, ReturnType<typeof setInterval>>();
const commentPollers = new Map<string, ReturnType<typeof setInterval>>();

/** Action creators: subscribe/unsubscribe */
export const subscribeTicket = (ticketId: string) => ({ type: 'tickets/subscribeTicket', payload: ticketId } as const);
export const unsubscribeTicket = (ticketId: string) => ({ type: 'tickets/unsubscribeTicket', payload: ticketId } as const);

async function pollTicket(ticketId: string, listenerApi: any) {
  try {
    const ticket = await apiFetch<Ticket>(`/api/tickets/${ticketId}`);
    if (ticket?.id) listenerApi.dispatch(ticketUpserted(ticket));
  } catch {
    // ticket may be forbidden or deleted; ignore and keep polling
  }
}

async function pollComments(ticketId: string, listenerApi: any) {
  try {
    const data = await apiFetch<TicketComment[] | { comments?: TicketComment[] }>(
      `/api/tickets/${ticketId}/comments`
    );
    const comments = Array.isArray(data) ? data : (data?.comments ?? []);
    listenerApi.dispatch(commentsSnapshotReplaced({ ticketId, comments }));
  } catch {
    // comments endpoint may fail transiently; retain last state
  }
}

// subscribe to backend polling for ticket + comments
startListening({
  predicate: (action) => action.type === 'tickets/subscribeTicket',
  effect: async (action, listenerApi) => {
    const ticketId = (action as any).payload as string;
    if (!ticketId || ticketPollers.has(ticketId)) return;

    await Promise.all([
      pollTicket(ticketId, listenerApi),
      pollComments(ticketId, listenerApi),
    ]);

    const ticketInterval = setInterval(() => {
      void pollTicket(ticketId, listenerApi);
    }, TICKET_POLL_MS);

    const commentsInterval = setInterval(() => {
      void pollComments(ticketId, listenerApi);
    }, COMMENTS_POLL_MS);

    ticketPollers.set(ticketId, ticketInterval);
    commentPollers.set(ticketId, commentsInterval);
    listenerApi.dispatch(markLiveSubscribed(ticketId));
  }
});

// unsubscribe
startListening({
  predicate: (action) => action.type === 'tickets/unsubscribeTicket',
  effect: async (action, listenerApi) => {
    const ticketId = (action as any).payload as string;
    const ticketInterval = ticketPollers.get(ticketId);
    const commentsInterval = commentPollers.get(ticketId);
    if (ticketInterval) clearInterval(ticketInterval);
    if (commentsInterval) clearInterval(commentsInterval);
    ticketPollers.delete(ticketId);
    commentPollers.delete(ticketId);
    listenerApi.dispatch(markLiveUnsubscribed(ticketId));
  }
});

// optional: auto-clean on store teardown (HMR/dev)
if (typeof import.meta !== 'undefined' && (import.meta as any).hot) {
  (import.meta as any).hot.dispose(() => {
    ticketPollers.forEach((id) => clearInterval(id));
    commentPollers.forEach((id) => clearInterval(id));
  });
}
